import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import { INDUSTRY_OPTIONS } from '@/services/business-profile.service';
import { groupIcon, groupedIndustryOptions, industryIcon, industryOption, type IndustryOption } from '@/lib/industries';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
}

/**
 * Searchable, grouped industry picker. A native <select> of 54 options gave
 * no way to search and no sense of the groups, which is how users think
 * ("I'm in food" before "I'm a bakery").
 */
export const IndustryPicker: React.FC<Props> = ({
  value, onChange, placeholder = 'Select your industry', disabled, invalid, className,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo<IndustryOption[]>(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [...INDUSTRY_OPTIONS];
    return INDUSTRY_OPTIONS.filter((o) =>
      o.label.toLowerCase().includes(term) || o.group.toLowerCase().includes(term) || o.value.includes(term));
  }, [q]);
  const groups = useMemo(() => groupedIndustryOptions(filtered), [filtered]);
  // Keyboard order must follow what's rendered (grouped), not the flat list.
  const flat = useMemo(() => groups.flatMap((g) => g.options), [groups]);

  useEffect(() => { setHighlight(0); }, [q]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const pick = (v: string) => { onChange(v); setOpen(false); setQ(''); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        const next = e.key === 'ArrowDown' ? Math.min(h + 1, flat.length - 1) : Math.max(h - 1, 0);
        listRef.current?.querySelector(`[data-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    }
    if (e.key === 'Enter' && flat[highlight]) { e.preventDefault(); pick(flat[highlight].value); }
  };

  const current = industryOption(value);
  const CurrentIcon = industryIcon(value);
  let idx = -1;

  return (
    <div className={clsx('relative', className)} ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          'flex w-full items-center gap-3 rounded-xl border bg-surface px-3 py-2.5 text-left transition-colors',
          'hover:border-border-strong focus:outline-none focus:ring-4 focus:ring-primary-soft',
          open ? 'border-primary' : invalid ? 'border-danger' : 'border-border',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className={clsx(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          current ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
        )}>
          <CurrentIcon size={17} />
        </span>
        <span className="min-w-0 flex-1">
          {current ? (
            <>
              <span className="block truncate text-sm font-semibold text-text-primary">{current.label}</span>
              <span className="block truncate text-2xs text-text-muted">{current.group}</span>
            </>
          ) : (
            <span className="block text-sm text-text-muted">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={15} className={clsx('shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-40 mt-1.5 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated"
          onKeyDown={onKeyDown}
        >
          <div className="relative border-b border-border p-2">
            <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search industries — e.g. bakery, software, clinic"
              className="w-full rounded-lg border border-border bg-surface py-2 pl-8 pr-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div ref={listRef} className="max-h-72 overflow-y-auto pb-1" role="listbox">
            {groups.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-text-muted">No industry matches “{q}”. Try “Other”.</p>
            ) : groups.map((g) => {
              const GroupIcon = groupIcon(g.group);
              return (
                <div key={g.group}>
                  <div className="sticky top-0 z-10 flex items-center gap-1.5 bg-surface-elevated/95 px-3 pb-1 pt-2.5 text-2xs font-semibold uppercase tracking-wide text-text-muted backdrop-blur">
                    <GroupIcon size={11} /> {g.group}
                  </div>
                  {g.options.map((o) => {
                    idx += 1;
                    const i = idx;
                    const Icon = industryIcon(o.value);
                    const selected = o.value === value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        data-idx={i}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => pick(o.value)}
                        className={clsx(
                          'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                          i === highlight ? 'bg-surface-alt' : 'hover:bg-surface-alt',
                        )}
                      >
                        <span className={clsx(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                          selected ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-secondary',
                        )}>
                          <Icon size={14} />
                        </span>
                        <span className={clsx('min-w-0 flex-1 truncate', selected ? 'font-semibold text-text-primary' : 'text-text-primary')}>
                          {o.label}
                        </span>
                        {selected && <Check size={14} className="shrink-0 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
